import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  Gamepad2,
  MessageCircle,
  Smartphone,
  Eye,
  Mail,
  FileText,
  Handshake,
  Package,
  Headphones,
  Download,
  Play,
  Star,
} from 'lucide-react';
import { SEO, SchemaMarkup } from '../components/seo';
import { LogoTicker } from '../components/LogoTicker';
import { BrochureButton } from '../components/BrochureButton';
import { useBrochure } from '../hooks/useBrochure';
import { TrustCountCard } from '../components/TrustBlock';
import { useLiveCount } from '../hooks/useLiveCount';
import { BASE_ORG_COUNT, getOrgCount } from '../lib/trust-count';

const features = [
  {
    icon: Award,
    title: 'Onafhankelijke registervertrouwenspersonen',
    text: 'Altijd LVV-gecertificeerd. Keuze voor een man of vrouw en voor een gesprek in het Nederlands of Engels. Online, telefonisch of op een neutrale locatie in de buurt — nooit op locatie van de organisatie. Beschikbaar op werkdagen van 09:00 tot 21:00 uur.',
  },
  {
    icon: Gamepad2,
    title: 'Het PuntUit-spel',
    text: 'Een fysiek spel met stellingen over omgangsvormen. Spelers reageren met groene, gele, oranje en rode kaarten en maken het onderwerp op een laagdrempelige en leuke manier bespreekbaar.',
  },
  {
    icon: MessageCircle,
    title: 'Gratis online spreekuur',
    text: 'Een laagdrempelige tussenstap zonder verplichtingen en zonder directe kosten voor de organisatie. In meer dan 70% van de gevallen is het spreekuur voldoende om het probleem zelf verder op te lossen.',
  },
  {
    icon: Smartphone,
    title: '24/7 zelfhulpplatform: praatuit.nl',
    text: 'Altijd toegang tot achtergrondinformatie, uitleg over de vertrouwenspersoon, alle contactmogelijkheden en een agenda om direct een spreekuurafspraak in te plannen.',
  },
  {
    icon: Eye,
    title: 'Early Awareness Tool',
    text: 'Op geaggregeerd niveau zie je voor welke onderwerpen binnen de organisatie meer aandacht is, zonder dat dit naar personen is te herleiden. Zo kun je eerder bijsturen.',
  },
  {
    icon: Mail,
    title: 'Periodieke awareness-mails',
    text: 'Per kwartaal een korte e-mail met een filmpje over een thema rond omgangsvormen, zodat de aandacht voor het onderwerp actief blijft.',
  },
  {
    icon: FileText,
    title: 'Jaarverslag / rapportage',
    text: 'Eén keer per jaar een jaarverslag over het platformbezoek, de spreekuurafspraken, de mate van opvolging en aanbevelingen voor de komende periode.',
  },
];

const steps = [
  {
    icon: Handshake,
    title: 'Kennismaking & voorstel',
    text: 'Een vrijblijvend kennismakingsgesprek waarin we laten zien hoe onze integrale aanpak organisaties helpt een sociaal veilige werkplek te creëren en te behouden.',
  },
  {
    icon: Package,
    title: 'Onboarding & activatie',
    text: 'Na akkoord ontvangt de organisatie twee e-mails — één voor de contactpersoon en een welkomstmail met korte video voor alle mensen — én het PuntUit-spel.',
  },
  {
    icon: Headphones,
    title: 'Direct beschikbaar & doorlopend actief',
    text: 'Vanaf de onboarding zijn de vertrouwenspersonen beschikbaar. PuntUit houdt het onderwerp actief via het spreekuur, awareness-mails, het zelfhulpplatform, de Early Awareness Tool en de jaarlijkse rapportage.',
  },
];

const tiers = [
  { range: 'Tot en met 10 personen', price: '€ 375', unit: 'per jaar' },
  { range: '11 t/m 250 personen', price: '€ 475', unit: 'per jaar', featured: true },
  { range: 'Per extra 250 personen', price: '+ € 475', unit: 'per jaar' },
];

// Echte Google-reviews, redactioneel ingekort
const reviews = [
  {
    quote: 'Het uitgangspunt van PuntUit is: maak een situatie zo snel mogelijk bespreekbaar en voorkom daarmee een escalatie. Een deskundige sparringpartner die meedenkt in oplossingen.',
    author: 'Koos Schipper',
    org: 'Stichting Allure',
  },
  {
    quote: 'Het ‘preventiespel’ is een leuke aanjager voor interne discussies en bewustwording. En een belafspraak inplannen is zo gedaan — laagdrempelig voor medewerkers.',
    author: 'Hans Lemm',
    org: 'Via Google',
  },
  {
    quote: 'Twee jaar samenwerking en het bevalt heel goed. Deskundige aanpak, heldere communicatie en laagdrempelig door het gratis online spreekuur. Wij raden dit bedrijf zeker aan.',
    author: 'Lesley Pelsmaeker',
    org: 'Via Google',
  },
];

const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps/place/?q=place_id:ChIJ4zIs8_nvxUcRmhLSVPFOeSM';

function HeroVideo() {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="hidden lg:block">
      <div className="aspect-[3/3] rounded-[2rem] overflow-hidden ring-1 ring-white/20 shadow-2xl relative">
        {playing ? (
          <video
            src="/video/b6378e44-771a9a75.mp4"
            controls
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="w-full h-full relative group cursor-pointer"
          >
            <img
              src="/images/extern-vertrouwenspersoon.jpg"
              alt="Bekijk de PuntUit video"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 transition-all flex items-center justify-center shadow-lg">
                <Play className="w-8 h-8 text-[#0284c7] ml-1" />
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

export function Home() {
  const { openBrochure } = useBrochure();
  const orgCount = useLiveCount(BASE_ORG_COUNT, getOrgCount);

  return (
    <>
      <SEO
        title="PuntUit | Externe Vertrouwenspersoon"
        description="Veel meer dan alleen een vertrouwenspersoon. PuntUit helpt problemen vroeg te signaleren met een bewustwordingsspel, een gratis online spreekuur, een zelfhulpplatform en awareness-mails."
        canonicalUrl="/"
      />
      <SchemaMarkup type="organization" />
      <SchemaMarkup
        type="website"
        breadcrumb={[{ name: 'Home', url: '/' }]}
      />
      <div className="pt-28">

        {/* ── HERO ── */}
        <section className="bg-[#0284c7] text-white overflow-hidden">
          <div className="container py-20 md:py-32">
            <div className="grid lg:grid-cols-[50%_1fr] gap-12 lg:gap-16 items-center">
              <div>
                <p className="text-xs font-semibold text-white/60 uppercase tracking-[0.2em] mb-6">
                  LVV-gecertificeerde Externe Vertrouwenspersonen
                </p>
                <h1 className="text-4xl md:text-5xl lg:text-[3.75rem] font-bold leading-[1.15] mb-8">
                  Veel meer dan alleen een vertrouwenspersoon.
                </h1>
                <p className="text-xl text-white/85 leading-relaxed mb-10 max-w-lg">
                  De beste vertrouwenspersoon is de vertrouwenspersoon die je niet nodig hebt. PuntUit helpt problemen vroeg te signaleren met een bewustwordingsspel, een kosteloos online spreekuur, een zelfhulpplatform en periodieke awareness-mails. En als het nodig is, biedt een externe LVV-gecertificeerde registervertrouwenspersoon hulp.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Link
                    to="/contact"
                    className="btn-primary text-base px-8 py-4"
                  >
                    Kennismaken
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    type="button"
                    onClick={openBrochure}
                    className="inline-flex items-center gap-2 text-base px-8 py-4 rounded-xl font-semibold bg-white text-[#0284c7] hover:bg-gray-100 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Download onze brochure
                  </button>
                </div>
              </div>

              <HeroVideo />
            </div>
          </div>
        </section>

        {/* ── LOGO TICKER ── */}
        <LogoTicker />

        {/* ── INTRO / POSITIONERING ── */}
        <section className="section-padding bg-white">
          <div className="container max-w-3xl">
            <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em] mb-4">Wat is PuntUit?</p>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Als organisatie wil je dat mensen weten dat er een vertrouwenspersoon voor ze is. In de praktijk weten mensen vaak niet wie de vertrouwenspersoon is of hoe ze die kunnen bereiken, terwijl intern gedacht wordt dat het goed geregeld is.
              </p>
              <p>
                PuntUit is meer dan een externe vertrouwenspersoon. Je krijgt een compleet programma voor bewustwording en preventie, met een bewustwordingsspel, een kosteloos online spreekuur, periodieke awareness-mails, een zelfhulpplatform en de Early Awareness Tool. Zo worden problemen vroeg opgepakt en houd je zicht op wat er speelt.
              </p>
            </div>
          </div>
        </section>

        {/* ── WAT KRIJG JE BIJ PUNTUIT ── */}
        <section className="section-padding bg-gray-50">
          <div className="container">
            <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Wat krijg je bij PuntUit?</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900">Een compleet programma, geen los aanspreekpunt.</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {features.map(({ icon: Icon, title, text }) => (
                <div
                  key={title}
                  className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#0284c7]/10 flex items-center justify-center mb-4">
                    <Icon className="w-4 h-4 text-[#0284c7]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1.5">{title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOE WERKT HET PROCES ── */}
        <section className="section-padding bg-white">
          <div className="container">
            <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Hoe werkt het proces?</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900">In drie stappen geregeld.</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {steps.map((step, i) => (
                <div key={step.title} className="relative">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-10 h-10 rounded-full bg-[#0284c7] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    {i < 2 && <div className="hidden lg:block flex-grow h-px bg-gray-200" />}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to="/contact" className="btn-primary">
                Plan een kennismakingsgesprek <ArrowRight className="w-4 h-4" />
              </Link>
              <BrochureButton variant="secondary" />
            </div>
          </div>
        </section>

        {/* ── TARIEVEN TEASER ── */}
        <section className="section-padding bg-gray-50">
          <div className="container max-w-5xl">
            <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Tarieven</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900">Eén jaarabonnement. Alles inbegrepen.</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {tiers.map((tier) => (
                <div
                  key={tier.range}
                  className={`rounded-2xl p-6 bg-white border ${tier.featured ? 'border-[#0284c7] shadow-md' : 'border-gray-100 shadow-sm'}`}
                >
                  {tier.featured && (
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-[#0284c7]/10 text-[#0284c7] px-2.5 py-1 rounded-md mb-3">Meest gekozen</span>
                  )}
                  <p className="text-gray-600 text-sm mb-2">{tier.range}</p>
                  <p className="text-2xl font-bold text-gray-900">{tier.price}</p>
                  <p className="text-gray-500 text-sm">{tier.unit}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:gap-8">
              <Link to="/tarieven" className="inline-flex items-center gap-1.5 text-[#0284c7] font-semibold hover:gap-2.5 transition-all">
                Bekijk alle tarieven <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/interne-vs-externe-vp" className="inline-flex items-center gap-1.5 text-[#0284c7] font-semibold hover:gap-2.5 transition-all">
                Waarom extern én goedkoper én beter is dan intern <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ── BEWIJS + TESTIMONIALS ── */}
        <section className="section-padding bg-white">
          <div className="container">
            <div className="grid md:grid-cols-3 gap-6 mb-14">
              <TrustCountCard />
              <div className="bg-[#0284c7] rounded-2xl p-8 text-white">
                <p className="text-4xl font-bold mb-2 tabular-nums">{orgCount}+</p>
                <p className="text-white/85 leading-relaxed">Meer dan {orgCount} organisaties vertrouwen op PuntUit.</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
                <p className="text-4xl font-bold mb-2 text-[#0284c7]">70%+</p>
                <p className="text-gray-700 leading-relaxed">In meer dan 70% van de gevallen is het online spreekuur voldoende en is er geen aanvullende inzet van een vertrouwenspersoon meer nodig.</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
              <div>
                <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Van onze klanten</p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Wat anderen zeggen.</h2>
              </div>
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0284c7] transition-colors"
              >
                <span className="font-bold text-gray-900">5,0</span>
                <span className="flex">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </span>
                <span>op Google · 11 reviews</span>
              </a>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {reviews.map((t) => (
                <div key={t.author} className="bg-gray-50 rounded-2xl p-8 border border-gray-100 flex flex-col">
                  <span className="flex mb-4">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </span>
                  <p className="text-gray-700 leading-relaxed flex-grow mb-6">{t.quote}</p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-semibold text-gray-900 text-sm">{t.author}</p>
                    <p className="text-gray-500 text-xs">{t.org}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CLOSING CTA ── */}
        <section className="section-padding bg-[#0284c7]">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center text-white">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">Klaar om het goed te regelen?</h2>
              <p className="text-xl text-white/80 mb-12 leading-relaxed">
                Een kennismakingsgesprek is vrijblijvend en kosteloos. Samen kijken we wat past bij jouw organisatie en cultuur.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-[#0284c7] px-12 py-5 rounded-xl font-bold text-lg hover:bg-gray-50 hover:text-[#0284c7] transition-all inline-flex items-center gap-3 ring-4 ring-white/20 whitespace-nowrap"
                >
                  Plan een kennismakingsgesprek
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <BrochureButton variant="outline-white" className="text-lg py-5" />
              </div>
              <p className="mt-10 text-white/60 text-sm">Liever direct bellen? <a href="tel:0851071256" className="text-white underline decoration-white/30">085 – 107 12 56</a></p>
            </div>
          </div>
        </section>

      </div>

    </>
  );
}
